import * as yup from "yup";
import {
    CreateEmissionsSourceFormValues,
    step3ValidationFields,
} from "./FormValues";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../Button";
import FormRadioGroup from "../FormRadioGroup";
import { dataStatusIds, dataStatusOptions } from "../../constants";
import Typography from "../Typography";
import FormTextInput from "../FormTextInput";
import FormMultiSelectDropdown from "../FormMultiSelectDropdown";
import { useEvidenceTypes } from "../../api/evidence";
import { FormDropdownOption } from "../FormDropdown";

export type Page3DataStatusProps = {
    onSubmit: (values: Step3DataStatusValues, finished: boolean) => void;
    onBack: (values: Step3DataStatusValues) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    defaultValues: Step3DataStatusValues;
};

export type Step3DataStatusValues = Pick<
    CreateEmissionsSourceFormValues,
    | "dataStatusId"
    | "justification"
    | "requiredEvidenceTypeIds"
    | "januaryEstimatedKgCO2e"
    | "februaryEstimatedKgCO2e"
    | "marchEstimatedKgCO2e"
    | "aprilEstimatedKgCO2e"
    | "mayEstimatedKgCO2e"
    | "juneEstimatedKgCO2e"
    | "julyEstimatedKgCO2e"
    | "augustEstimatedKgCO2e"
    | "septemberEstimatedKgCO2e"
    | "octoberEstimatedKgCO2e"
    | "novemberEstimatedKgCO2e"
    | "decemberEstimatedKgCO2e"
>;

const validationSchema: yup.SchemaOf<Step3DataStatusValues> = yup.object(
    step3ValidationFields
);

const Page3DataStatus = ({
    onSubmit,
    onBack,
    onCancel,
    isSubmitting,
    defaultValues,
}: Page3DataStatusProps) => {
    const { evidenceTypes, isLoading: isLoadingEvidenceTypes } =
        useEvidenceTypes();

    const evidenceTypeOptions: FormDropdownOption<number>[] = evidenceTypes.map(
        (evidenceType) => ({
            value: evidenceType.id,
            label: evidenceType.name,
        })
    );

    const methods = useForm<Step3DataStatusValues>({
        mode: "onSubmit",
        resolver: yupResolver(validationSchema),
        defaultValues,
    });

    const handleBackClick = async () => {
        await methods.trigger();

        if (methods.formState.isValid) {
            onBack(methods.getValues());
        }
    };

    const handleSubmit = (values: Step3DataStatusValues) => {
        if (values.dataStatusId === dataStatusIds.estimated) {
            values.januaryEstimatedKgCO2e = 0;
            values.februaryEstimatedKgCO2e = 0;
            values.marchEstimatedKgCO2e = 0;
            values.aprilEstimatedKgCO2e = 0;
            values.mayEstimatedKgCO2e = 0;
            values.juneEstimatedKgCO2e = 0;
            values.julyEstimatedKgCO2e = 0;
            values.augustEstimatedKgCO2e = 0;
            values.septemberEstimatedKgCO2e = 0;
            values.octoberEstimatedKgCO2e = 0;
            values.novemberEstimatedKgCO2e = 0;
            values.decemberEstimatedKgCO2e = 0;
        }

        onSubmit(values, true);
    };

    return (
        <form
            onSubmit={methods.handleSubmit(handleSubmit)}
            className="rounded-xl bg-surface-light-1 p-6 space-y-4"
        >
            <Typography variant="title-large" component="h2" className="mb-6">
                Data status
            </Typography>
            <Controller
                name="dataStatusId"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormRadioGroup
                        required
                        label="Select data status"
                        options={dataStatusOptions}
                        error={methods.formState.errors.dataStatusId}
                        defaultValue={value}
                        onChange={onChange}
                        value={value}
                    />
                )}
            />
            <FormTextInput
                required
                label="Justification"
                placeholder="Enter justification"
                {...methods.register("justification")}
                error={methods.formState.errors.justification}
            />
            <Controller
                name="requiredEvidenceTypeIds"
                control={methods.control}
                render={({ field: { onChange, value } }) => {
                    return (
                        <FormMultiSelectDropdown
                            options={evidenceTypeOptions}
                            label="Required evidence types"
                            text="Select required evidence types"
                            values={value}
                            onSelect={onChange}
                            isLoadingOptions={isLoadingEvidenceTypes}
                        />
                    );
                }}
            />
            <div className="flex justify-end pt-4">
                <Button variant="transparent" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="outline"
                    type="button"
                    className="mr-4"
                    onClick={handleBackClick}
                >
                    Previous
                </Button>
                <Button disabled={isSubmitting} type="submit">
                    Add emissions source
                </Button>
            </div>
        </form>
    );
};

export default Page3DataStatus;
